import * as types from '../../../types';
import {RepositoryFactory} from '../../../../repositories/RepositoryFactory';
import * as notify from '../../../../lang/en/notify';
import Decimal from 'decimal.js';

const ShopifyShippingZoneRepository = RepositoryFactory.get(
    'acadia/shopify/store-properties/shipping-zone');
const CUSTOM_SHIPPING_STRING = '[CUSTOM] ';

export default {
  state: {
    zones: [],
    options: [],
    selected: {
      price: 0,
    },
    customOptions: [],
    customSelected: {
      id: null,
    },
    customPrice: '0.00',
    address: {
      'first_name': null,
      'address1': null,
      'phone': null,
      'city': null,
      'zip': null,
      'province': null,
      'country': null,
      'last_name': null,
      'address2': null,
      'company': null,
      'name': null,
    },
    shippingLines: []
  },
  mutations: {
    [types.ORDER_SET_SHIPPING_ADDRESS](state, address) {
      for (let item in address) {
        if (state.address.hasOwnProperty(item)) {
          state.address[item] = address[item];
        }
      }
    },
    [types.ORDER_SET_SHIPPING_CUSTOM_OPTIONS](state, options) {
      let customOptions = [];

      for (let option in options) {
        let customOption = {...options[option]};
        customOption.name = CUSTOM_SHIPPING_STRING + customOption.name;
        customOptions.push(customOption);
      }

      // Shipping line erased from Shopify
      if(!customOptions.length) {
        const option = {...state.shippingLines[0]};
        option.name = option.title;
        customOptions.push(option)
      }

      state.customOptions = customOptions;
    },
    [types.ORDER_SET_SHIPPING_CUSTOM_SELECTED](state, payload) {
      state.customSelected = payload;
    },
    [types.ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE](state, price) {
      state.customPrice = price;
    },
    [types.ORDER_SET_SHIPPING_OPTIONS](state, options) {
      const shippingOptions = [...options];

      shippingOptions.push({
        id: 0,
        max_order_subtotal: null,
        min_order_subtotal: null,
        name: 'Custom',
        price: 0.00,
        shipping_zone_id: 0,
      });

      state.options = shippingOptions;
    },
    [types.ORDER_SET_SHIPPING_SELECTED](state, payload) {
      state.selected = payload;
    },
    [types.ORDER_SET_SHIPPING_ZONES](state, shippingZones) {
      state.zones = shippingZones;
    },
    [types.ORDER_SET_SHIPPING_LINES](state, shippingLines) {
      state.shippingLines = shippingLines;
    }
  },
  actions: {
    async [types.ORDER_SHIPPING_INIT]({commit, dispatch, getters, rootState}, order) {
      const {region} = rootState.order;
      // Set Shipping Lines & Shipping Address
      commit(types.ORDER_SET_SHIPPING_LINES, order.shipping_lines);
      commit(types.ORDER_SET_SHIPPING_ADDRESS, order.shipping_address);
      // Set the Shipping Price
      const shippingPrice = getters[types.ORDER_GET_SHIPPING_TOTAL_PRICE];
      await dispatch(types.ORDER_SHIPPING_RESET, shippingPrice);
      // Get available shipping zones and set active zone
      await dispatch(types.ORDER_SHIPPING_FETCH_ZONES, region);
      const zone = getters[types.ORDER_GET_SHIPPING_ACTIVE_ZONE];

      // If zone is price based return those rates. If it is using a third party provider use that, else it is empty.
      const shippingRates = (zone && zone['price_based_shipping_rates'].length && zone['price_based_shipping_rates'])
          || (zone && zone['carrier_shipping_rate_providers'].length && [{
            id: zone['carrier_shipping_rate_providers'][0].id,
            max_order_subtotal: null,
            min_order_subtotal: null,
            name: (order.shipping_lines && order.shipping_lines.length) ? order.shipping_lines[0].title : zone['name'],
            price: (order.shipping_lines && order.shipping_lines.length) ? order.shipping_lines[0].price : 0.00,
            shipping_zone_id: zone['carrier_shipping_rate_providers'][0].shipping_zone_id,
        }]) || [];

      // Set the Available Shipping Options & Custom Shipping Options
      commit(types.ORDER_SET_SHIPPING_OPTIONS, shippingRates);
      commit(types.ORDER_SET_SHIPPING_CUSTOM_OPTIONS, shippingRates);
      // Apply the Selected Shipping Line
      dispatch(types.ORDER_SHIPPING_APPLY_SELECTED);
    },
    async [types.ORDER_SHIPPING_FETCH_ZONES]({commit}, region) {
      const response = await ShopifyShippingZoneRepository.getShippingZones(region);
      const {shipping_zones: shippingZones} = response.data;
      commit(types.ORDER_SET_SHIPPING_ZONES, shippingZones);
    },
    [types.ORDER_SHIPPING_APPLY_SELECTED]({state, commit, dispatch, getters}) {
      // Regular Shipping Line
      const shippingLine = state.shippingLines[0];
      const shippingPrice = Number(getters[types.ORDER_GET_SHIPPING_TOTAL_PRICE]);
      const shippingDiscountedPrice = Number(getters[types.ORDER_GET_SHIPPING_DISCOUNTED_PRICE]);
      const shippingTitle = shippingLine.title;
      const roundingTolerance = 0.05;
      const minPrice = shippingPrice - roundingTolerance;
      const maxPrice = shippingPrice + roundingTolerance;
      let selectedShippingLine = state.options.find(rate => {
        const price = new Decimal(rate['price']);
        const matchingName = shippingTitle.includes(rate['name']);
        const matchingPrice = price.gte(minPrice) && price.lte(maxPrice);
        return matchingName && matchingPrice;
      });
      const isCustomShipping = shippingTitle.includes(CUSTOM_SHIPPING_STRING);
      const hasMismatchedPrice = selectedShippingLine ? !(new Decimal(shippingDiscountedPrice)).eq(selectedShippingLine['price']) : true;

      /*
       * Check if custom shipping line or if it has a price mismatch.
       * Zonos is leading to price mismatches, which is messing up the Shopify
       * draft order API since that relies on shipping price being consistent.
       * So a quick solution is to force a custom shipping line when there is a
       * price mismatch.
       */
      if (isCustomShipping || hasMismatchedPrice) {
        // Set Shipping line to Custom
        selectedShippingLine = state.options.find(option => option.id === 0);
        // Custom Shipping Line
        const customSelectedOrder = state.customOptions.find(option =>
          option.name.includes(shippingTitle),
        ) ?? state.customOptions[0];

        commit(types.ORDER_SET_SHIPPING_CUSTOM_SELECTED, customSelectedOrder);
        commit(types.ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE, shippingDiscountedPrice);
      }

      if (hasMismatchedPrice) {
        dispatch(types.WARNING, notify.ORDER_SHIPPING_PRICE_MISMATCH);
      }

      commit(types.ORDER_SET_SHIPPING_SELECTED, selectedShippingLine);
    },
    [types.ORDER_SHIPPING_RESET]({commit}, shippingPrice) {
      const shippingSelected = {
        price: 0,
      };
      let shippingCustom = {
        id: null,
      };
      commit(types.ORDER_SET_SHIPPING_ZONES, []);
      commit(types.ORDER_SET_SHIPPING_OPTIONS, []);
      commit(types.ORDER_SET_SHIPPING_SELECTED, shippingSelected);
      commit(types.ORDER_SET_SHIPPING_CUSTOM_OPTIONS, []);
      commit(types.ORDER_SET_SHIPPING_CUSTOM_SELECTED, shippingCustom);
      commit(types.ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE, shippingPrice);
    },
    [types.ORDER_SHIPPING_SELECT]({commit, state}, selectedId) {
      const selectedShippingLine = [...state.options].find(option => {
        return selectedId === option.id;
      });
      commit(types.ORDER_SET_SHIPPING_SELECTED, selectedShippingLine);
    },
    [types.ORDER_SHIPPING_CUSTOM_SELECT]({commit, state}, selectedId) {
      const selectedShippingLine = [...state.customOptions].find(option => {
        return selectedId === option.id;
      });
      commit(types.ORDER_SET_SHIPPING_CUSTOM_SELECTED, selectedShippingLine);
    },
  },
  getters: {
    [types.ORDER_GET_SHIPPING_ADDRESS](state) {
      return {...state.address};
    },
    [types.ORDER_GET_SHIPPING_LINE](state) {
      if (state.selected.id === 0) {
        return {
          custom: true,
          title: state.customSelected.name,
          price: state.customPrice,
        };
      }

      return {
        custom: false,
        title: state.selected.name,
        price: state.selected.price,
      };
    },
    [types.ORDER_GET_SHIPPING_TOTAL_PRICE](state) {
      const reducer = (total, shippingLine) => {
        return total.plus(shippingLine.price)
      };
      const totalPrice = state.shippingLines.reduce(reducer, new Decimal(0));
      return totalPrice.toFixed(2);
    },
    [types.ORDER_GET_SHIPPING_DISCOUNTED_PRICE](state) {
      const reducer = (total, shippingLine) => {
        return total.plus(shippingLine.discounted_price || 0)
      };
      const totalPrice = state.shippingLines.reduce(reducer, new Decimal(0));
      return totalPrice.toFixed(2);
    },
    [types.ORDER_GET_SHIPPING_ACTIVE_ZONE](state) {
      if (state.shippingLines.length < 1) return {};

      const shippingLine = state.shippingLines[0];

      const priceBasedZone = state.zones.find(zone => {
        return zone['price_based_shipping_rates'].find(rate => (
            shippingLine['title'].includes(rate['name'])
        ));
      });

      if(priceBasedZone) {
        return priceBasedZone
      }
      if(shippingLine.title.includes('International')) {
        return state.zones.find(zone =>
            zone['name'].includes('Zonos') || zone['carrier_shipping_rate_providers'].length
        );
      }
      return null;
    }
  },
};
